import { useCallback, useEffect, useRef, useState } from "react";

type SelectOptionValue = string | number;

type SelectProps = {
  label?: string;
  name: string;
  value: SelectOptionValue;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: { value: SelectOptionValue; label: string }[];
  required?: boolean;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

export const Select = ({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  error,
  placeholder,
  disabled = false,
  className,
}: SelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const activeIndexRef = useRef(-1);

  const borderColor = error ? "border-primary-600" : `border-gray-300 ${disabled ? "" : "hover:border-gray-400"}`;
  const resolvedPlaceholder = placeholder ?? "Selecione...";

  const selectedOption = options.find(
    (o) => o.value.toString() === value.toString(),
  );
  const displayText = selectedOption?.label ?? resolvedPlaceholder;
  const hasSelection = selectedOption !== undefined;

  const triggerChange = useCallback(
    (newValue: string) => {
      const event = {
        target: { name, value: newValue },
        currentTarget: { name, value: newValue },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;
      onChange(event);
    },
    [name, onChange],
  );

  const selectOption = useCallback(
    (optValue: string) => {
      triggerChange(optValue);
      setOpen(false);
    },
    [triggerChange],
  );

  // Close on click outside
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          activeIndexRef.current = -1;
        } else if (activeIndexRef.current >= 0) {
          selectOption(options[activeIndexRef.current].value.toString());
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          activeIndexRef.current = 0;
        } else {
          activeIndexRef.current = Math.min(
            activeIndexRef.current + 1,
            options.length - 1,
          );
          scrollToActive();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          activeIndexRef.current = options.length - 1;
        } else {
          activeIndexRef.current = Math.max(activeIndexRef.current - 1, 0);
          scrollToActive();
        }
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  const scrollToActive = () => {
    const list = listRef.current;
    const items = list?.children;
    if (items && activeIndexRef.current >= 0 && items[activeIndexRef.current]) {
      (items[activeIndexRef.current] as HTMLElement).scrollIntoView({
        block: "nearest",
      });
    }
  };

  return (
    <div
      className={`w-full sm:w-auto relative ${className ?? ""}`.trim()}
      ref={containerRef}
    >
      {label && (
        <label
          htmlFor={name}
          className="block mb-1 text-gray-600 text-sm font-medium"
        >
          {label} {required && <span className="text-primary-600">*</span>}
        </label>
      )}

      <button
        type="button"
        id={name}
        name={name}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        className={`w-full py-3 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 flex items-center justify-between gap-2 ${borderColor} ${disabled ? "cursor-not-allowed bg-gray-100 text-gray-500" : "cursor-pointer bg-white text-left"} ${!hasSelection ? "text-gray-400" : ""}`}
      >
        <span className="truncate">{displayText}</span>
        <svg
          className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {options.map((opt, idx) => {
            const isActive = idx === activeIndexRef.current;
            const isSelected = opt.value.toString() === value.toString();
            return (
              <li
                key={opt.value.toString()}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => {
                  activeIndexRef.current = idx;
                }}
                onMouseDown={() => selectOption(opt.value.toString())}
                className={`px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-gray-100 ${
                  isSelected
                    ? "bg-primary-600 text-white hover:bg-primary-700"
                    : isActive
                      ? "bg-gray-100"
                      : ""
                }`}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="text-sm text-primary-600 mt-1">{error}</p>}
    </div>
  );
};
